import Vue from 'vue';
import snakeCase from 'lodash/snakeCase';
import gql from 'graphql-tag';

import { PAID_COUPON_KEY, PAID_COUPON_AMOUNT_KEY } from '@breakmedia/prime-vue-coupon/src/config/constants';

const orderPartner = Vue.localStorage.get('orderPartner');
const orderReferrer = Vue.localStorage.get('orderReferrer');
const primeCampaign = Vue.localStorage.get('primeCampaign');

Vue.localStorage.order = {
    type: Object,
    default: {
        order: {}
    }
};

Vue.localStorage.orderPartner = {
    type: String,
    default: ''
};

Vue.localStorage.orderReferrer = {
    type: String,
    default: ''
};

Vue.localStorage.primeCampaign = {
    type: String,
    default: ''
};

export default {
    namespaced: true,
    state: {
        order: null,
        billingAddressId: null,
        shippingAddressId: null,
        partner: orderPartner,
        referrer: orderReferrer,
        primeCampaign: primeCampaign,
    },
    mutations: {
        setOrder(state, order) {
            state.order = order;
        },
        setBillingAddress(state, value) {
            state.billingAddressId = value;
        },
        setShippingAddress(state, value) {
            state.shippingAddressId = value;
        },
        setPartner(state, partner) {
            state.partner = partner;
        },
        setReferrer: (state, referrer) => state.referrer = referrer,
        setPrimeCampaign: (state, primeCampaign) => state.primeCampaign = primeCampaign,
    },
    actions: {
        applyPartner({ commit }, partner) {
            Vue.localStorage.set('orderPartner', partner);
            commit('setPartner', partner);
        },
        applyReferrer({ commit }, referrer) {
            Vue.localStorage.set('orderReferrer', referrer);
            commit('setReferrer', referrer);
        },
        applyPrimeCampaign({ commit }, primeCampaign) {
            Vue.localStorage.set('primeCampaign', primeCampaign);
            commit('setPrimeCampaign', primeCampaign);
        },
        async createOrder({ state, commit, dispatch, rootState }, { basket, address, email }) {
            const billingAddress = address || rootState.BillingAddressForm.address;
            if (!email) {
                if (rootState.Registration && rootState.Registration.email) {
                    email = rootState.Registration.email;
                } else {
                    email = (await dispatch('Customer/loadCustomerData', null, { root: true })).email;
                }
            }

            if (!email) {
                throw 'No email!';
            }

            let phone = '';
            let company = '';
            let uid = '';
            if (!rootState.Registration || !rootState.Registration.phone) {
                await dispatch('BillingAddressForm/loadAddress', null, { root: true });
            }
            phone = rootState.Registration.phone;
            uid = rootState.Registration.uid;
            company = rootState.Registration.company;

            let billingAddressData = {};
            for (const key of Object.keys(billingAddress)) {
                if (key !== 'id') {
                    billingAddressData[snakeCase(key)] = billingAddress[key];
                }
            }

            let shippingAddressData = {};
            if (rootState.CheckoutAddressStep && rootState.CheckoutAddressStep.otherAddress) {
                const shippingAddress = rootState.ShippingAddressForm.address;
                for (const key of Object.keys(shippingAddress)) {
                    if (key !== 'id') {
                        shippingAddressData[snakeCase(key)] = shippingAddress[key];
                    }
                }
            } else {
                shippingAddressData = billingAddressData;
            }

            basket.items = Object.values(basket.items);
            basket.billing = billingAddressData;
            basket.shipping = shippingAddressData;
            basket.options.partner = state.partner;
            basket.options.referrer = state.referrer;
            basket.options.prime_campaign = state.primeCampaign;
            basket.options.email = email;
            basket.options.phone = phone;
            basket.options.uid = uid;
            basket.options.company = company;

            if (billingAddress) {
                this.dispatch('Customer/validatePriceGroup', { addressData: billingAddress }, { root: true });
            }

            if (rootState.Coupon.paidCoupons && Object.keys(rootState.Coupon.paidCoupons).length) {
                basket.options[PAID_COUPON_KEY] = rootState.Coupon.paidCoupons;
            }
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation createOrder(
                            $basket: BasketInput!,
                            $language: String!,
                            $country: String,
                            $currency: String,
                            $shippingProvider: Int,
                        ) {
                            createOrder(
                                basket: $basket,
                                language: $language,
                                country: $country,
                                currency: $currency,
                                shippingProvider: $shippingProvider
                            ) {
                                id
                                transaction_id
                            }
                        }
                    `,
                    variables: {
                        basket: basket,
                        country: address && address.country ? address.country : Vue.$order.getDeliveryCountry(),
                        currency: Vue.$currency.getSelected(),
                        language: Vue.$language.getLanguage(),
                        shippingProvider: rootState.ShippingSelector.selectedShippingProvider,
                    }
                })
                .then(r => r.data.createOrder)
                .then(order => {
                    commit('setOrder', order);
                    return order;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        setBillingAddressId({ commit }, value) {
            commit('setBillingAddress', value);
        },
        setShippingAddressId({ commit }, value) {
            commit('setShippingAddress', value);
        },
        getOrderByFilters({}, filters) {
            if (Object.keys(filters).length > 0) {
                const transactionId = filters.transactionId;
                delete filters.transactionId;
                return Vue.$graphql
                    .query({
                        query: gql`
                        query order($transactionId: String!, $filters: JSON) {
                            order(transactionId: $transactionId, filters: $filters) {
                                id
                                success_date
                                price_total
                                Options
                                vat_amount
                                ShippingPrices {
                                    price
                                    company_department_id
                                }
                                OrderItems {
                                    id
                                    Options
                                    quantity
                                    price_original
                                    price_vat
                                    price_total
                                    discount
                                    product_catalog_item_id
                                    master_catalog_item_id
                                    is_pickup
                                    fees {
                                        id
                                        name
                                        value
                                        is_percent
                                        per_product
                                    }
                                }
                                Fees {
                                    type
                                    value
                                }
                                Currency
                            }
                        }
                    `,
                        variables: {
                            transactionId,
                            filters,
                        },
                    })
                    .then(r => r.data.order);
            } else {
                return Promise.resolve(null);
            }
        },
    }
};
